import {
  documentRouteStageParticipantSetResult,
  documentSignatureCreate,
} from '@/requests/document'
import { setSnackbar } from 'best-modules/plugins'
import { openDialog } from '@/dialog'
import { DocumentListItem } from '@/utils/types/document'
import { computed, ComputedRef, inject } from 'vue'
import { rerenderKey } from '@/injectionKeys'
import { useStore } from '@/plugins'
import {
  SignedDocument,
  SignDocumentDialogData,
} from '@/dialog/components/sign-document/types'

export function useParticipantResult(
  agreementType: 'agreement' | 'signature' | null,
  documents: ComputedRef<DocumentListItem[]>
) {
  const rerender = inject(rerenderKey, () => void [0])
  const store = useStore()
  const userId = computed(() => store.state.user.userData?.id)

  const isEcpRequired = (documentSource: DocumentListItem): boolean => {
    if (agreementType === 'agreement') {
      return documentSource.definition?.isApprovalEcpRequired
    }
    if (agreementType === 'signature') {
      return documentSource.definition?.isSigningEcpRequired
    }

    return false
  }

  const getCurrentUserParticipantId = (
    document: DocumentListItem
  ): number | null => {
    let participantId: number | null = null

    for (const stage of document.stages || []) {
      participantId =
        stage?.participants.find(p => p.userId === userId.value)?.id || null
      if (participantId) {
        break
      }
    }
    return participantId
  }
  const documentsWithEcpRequired = computed<DocumentListItem[]>(() => {
    return documents.value.filter(isEcpRequired)
  })

  const handleParticipantActionError = (e: any) => {
    const msg =
      e?.response?.data?.errors?.message?.[0] ||
      e?.response?.data?.message ||
      e?.message ||
      'Помилка виконання дії'
    setSnackbar({ text: msg, color: 'error' })
  }

  const setParticipantResult = (
    documents: DocumentListItem[],
    resultId: number
  ) => {
    const participantIds = documents
      .map(getCurrentUserParticipantId)
      .filter(Boolean) as number[]

    return documentRouteStageParticipantSetResult(participantIds, resultId)
  }

  const createParticipantSignatures = (signatures: SignedDocument[]) => {
    if (!signatures.length) {
      return Promise.reject(new Error('Документ не було підписано'))
    }

    return documentSignatureCreate(
      signatures.map(signature => {
        return {
          docTypeDocumentId: signature.meta?.documentId,
          documentSigned: signature.documentSigned,
          signingDate: signature.signingDate,
          fullCertificate: signature.fullCertificate,
          signatureContextTypeId: 1,
          signatureContextId: signature.meta?.participantId,
        }
      })
    )
  }

  const signDocuments = async () => {
    const documentFiles: SignDocumentDialogData['documents'] = []

    for (const document of documentsWithEcpRequired.value) {
      for (const d of document.document) {
        documentFiles.push({
          document: d,
          uploadFilesParams: {
            mainFolder: 'document',
            nameFolder: 'signature',
            prefixFolder: document.uuid,
          },
          meta: {
            documentId: d.id,
            participantId: getCurrentUserParticipantId(document),
          },
        })
      }
    }

    return openDialog({
      name: 'SignDocument',
      dialogData: {
        documents: documentFiles,
      },
      params: {
        hideActionButtons: true,
        cb: async (signatures: SignedDocument[]) => {
          if (!Array.isArray(signatures)) return

          try {
            await createParticipantSignatures(signatures)
            setSnackbar({ text: 'Документ успішно підписано' })
          } catch (e) {
            handleParticipantActionError(e)
            throw e
          }
        },
      },
    })
  }

  const agree = async (resultId: number) => {
    if (resultId === 1 && documentsWithEcpRequired.value.length) {
      await signDocuments().catch(handleParticipantActionError)
    }

    await setParticipantResult(documents.value, resultId).catch(
      handleParticipantActionError
    )

    rerender()
  }

  return { agree }
}
